import { Component, computed, inject, input } from '@angular/core';
import { SensorUnitPipe } from '@shared/pipes/sensor-unit.pipe';
import { FlatItemDto, FlatYearlyMetricsDto } from '@artemis-software/wr-api';
import { monthNameDe } from '@/utils/date-utils';

export enum SensorInstallationType {
  HEAT = 'HEAT',
  HOT_WATER = 'HOT_WATER',
}

@Component({
  standalone: false,
  selector: 'wr-yearly-unit-summary-chart',
  templateUrl: './yearly-unit-summary-chart.component.html',
  styleUrl: './yearly-unit-summary-chart.component.scss'
})
export class YearlyUnitSummaryChartComponent {
  readonly items = input.required<FlatYearlyMetricsDto[]>();          // full list (don’t mutate!)
  readonly metric = input.required<SensorInstallationType>();         // HEAT or HOT_WATER
  readonly year = input.required<number>();
  readonly chartKey = input<string>('');

  private readonly unitPipe = inject(SensorUnitPipe);

  // months/labels
  readonly months = computed(() => Array.from({ length: 12 }, (_, i) => i + 1));
  readonly monthLabels = computed(() => this.months().map(m => monthNameDe(m, 'short')));

  private getMetricFor(dto: FlatYearlyMetricsDto) {
    return this.metric() === SensorInstallationType.HEAT ? dto.heatMetrics : dto.hotWaterMetrics;
  }

  private unitKey(u: FlatItemDto): string {
    return u.id ?? `unit:${u.flatNumber ?? 'na'}`;
  }

  readonly unitLabel = computed(() => {
    const first = this.items().map(d => this.getMetricFor(d)).find(Boolean);
    return first ? this.unitPipe.transform(first!.baseUnit) : '';
  });

  readonly titleText = computed(() =>
    this.metric() === SensorInstallationType.HEAT ? 'Heizung' : 'Warmwasser'
  );

  // map: unitKey -> monthly array (length 12)
  readonly monthlyByUnit = computed(() => {
    const map = new Map<string, number[]>();
    const months = this.months();
    // IMPORTANT: do not mutate items() externally; rebuild new arrays here
    for (const dto of this.items()) {
      const key = this.unitKey(dto.flat);
      const metric = this.getMetricFor(dto);
      const row = months.map(m => metric?.metrics?.find(x => x.month === m)?.total ?? 0);
      map.set(key, row);
    }
    return map;
  });

  readonly unitsOrdered = computed<FlatItemDto[]>(() => {
    const arr = this.items().map(d => d.flat).slice(); // copy!
    arr.sort((a, b) => {
      const an = parseInt(String(a.flatNumber ?? ''), 10);
      const bn = parseInt(String(b.flatNumber ?? ''), 10);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) return bn - an;
      return String(a.id ?? '').localeCompare(String(b.id ?? ''), 'de');
    });
    return arr;
  });

  readonly traces = computed<any[]>(() => {
    return this.unitsOrdered().map(u => {
      const key = this.unitKey(u);
      const y = (this.monthlyByUnit().get(key) ?? new Array(12).fill(0)).slice();
      return {
        type: 'bar',
        uid: `${this.metric()}-${this.year()}-${this.chartKey()}-${key}`, // ← add this
        name: `TOP ${u.flatNumber ?? '–'}`,
        x: this.monthLabels().slice(),
        y,
        marker: { color: u.color },
        hovertemplate: `TOP ${u.flatNumber ?? '–'}<br>%{x}: %{y:.4f} ${this.unitLabel()}<extra></extra>`,
      };
    });
  });

  readonly totalTrace = computed<any>(() => {
    const totals = this.months().map((_, i) => {
      let s = 0;
      for (const arr of this.monthlyByUnit().values()) s += arr[i] ?? 0;
      return s;
    });
    return {
      type: 'scatter',
      uid: `${this.metric()}-${this.year()}-${this.chartKey()}-TOTAL`, // ← add this
      mode: 'lines+markers',
      name: 'Gesamt',
      x: this.monthLabels().slice(),
      y: totals,
      line: { width: 1 },
      marker: { size: 4 },
      hovertemplate: `Gesamt<br>%{x}: %{y:.4f} ${this.unitLabel()}<extra></extra>`,
    };
  });

  readonly data = computed(() => [...this.traces(), this.totalTrace()]); // new array each time

  readonly config = { displayModeBar: false, responsive: true };

  readonly layout = computed(() => ({
    barmode: 'stack',
    hovermode: 'x unified',
    bargap: 0.36,
    margin: { l: 56, r: 16, t: 44, b: 72 },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    title: { text: this.titleText(), font: { size: 16 }, x: 0, y: 0.98, xanchor: 'left', yanchor: 'top' },
    xaxis: {
      type: 'category',
      categoryorder: 'array',
      categoryarray: this.monthLabels().slice(),
      tickfont: { size: 14 },
      title: { text: '' },
    },
    yaxis: {
      title: { text: this.unitLabel() },
      zeroline: false,
      gridcolor: 'rgba(0,0,0,0.08)',
    },
    legend: {
      orientation: 'h', y: -0.2, x: 0.5, xanchor: 'center', yanchor: 'top',
      bgcolor: 'rgba(0,0,0,0)', font: { size: 12 },
    },
    uirevision: `${this.metric()}-${this.year()}-${this.chartKey()}`,
  }));
}
